import {
  Account,
  PayStatement,
  PayStatementEarning,
  PayStatementTaxEntry,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import sumBy from "lodash/sumBy";

import {
  formatPayPeriodRange,
  formatV3Address,
  parsePayrollAmount
} from "../utils";

import { selectorPayStatementWorkLogIds } from "./selectorPayStatementWorkLogIds";

const EMPTY_VALUE = "—";

/**
 * Full detail for a single paystub, used by the paystub details page.
 *
 * This is an aggregate of three v3 reads — the `PayStatement`, the pay period's
 * work logs, and the payee/payer `Account`s — so it has no single SDK
 * counterpart. Everything that maps 1:1 onto an SDK model is carried as that
 * model (`earnings`, `payeeTaxes`, `workLogs`) rather than being re-shaped.
 */
export type PaystubDetails = {
  id: string;
  /** Human-readable paystub number shown in the title, e.g. "00ABC-123". */
  paystubNumber: string;
  /** Company that created the payroll, shown next to the title. */
  createdBy: string;
  payPeriodRange: string;
  paidDate?: Date;
  employeeName: string;
  employeeAddress: string;
  earnings: PayStatementEarning[];
  grossPayThisPeriod: number;
  grossPayYearToDate: number;
  /** Worker tax withholdings; v3 has no pre-tax/per-state split. */
  payeeTaxes: PayStatementTaxEntry[];
  deductionTotalThisPeriod: number;
  deductionTotalYearToDate: number;
  netPayThisPeriod: number;
  netPayYearToDate: number;
  /** Masked payout method shown next to net pay, e.g. "Direct deposit - Chase Bank [••••2345]". */
  payoutMethodLabel: string;
  workLogs: WorkLog[];
  workLogsTotal: number;
  /** Submitted-but-unpaid work logs for this pay period; drives the warning alert when non-empty. */
  excludedWorkLogs: WorkLog[];
};

/** Worker display name from the account's compliance entity (legal name). */
const selectEmployeeName = (account?: Account): string => {
  const name = account?.complianceEntity?.individualLegalName;
  return (
    name?.fullLegalName ||
    [name?.givenName, name?.familyName].filter(Boolean).join(" ") ||
    account?.complianceEntity?.legalName ||
    EMPTY_VALUE
  );
};

/** Worker mailing address (falls back through the CE address fields). */
const selectEmployeeAddress = (account?: Account): string => {
  const ce = account?.complianceEntity;
  const address =
    ce?.mailingAddress ??
    ce?.address ??
    ce?.physicalAddress ??
    ce?.legalAddress;
  return formatV3Address(address) || EMPTY_VALUE;
};

type Input = {
  payStatement: PayStatement;
  /** All work logs for the pay period; filtered to this statement's sources here. */
  workLogs: WorkLog[];
  /** The payee (`payStatement.payeeAccountId`) account — supplies name + address. */
  payeeAccount?: Account;
  /** The owning payer account (`payStatement.payerAccountId`) — supplies "Created by". */
  payerAccount?: Account;
};

/**
 * Adapt a v3 `PayStatement` (+ the period's work logs and the payee/payer
 * accounts) into the paystub detail view model.
 *
 * TODO(v3): remaining gaps that fall back to `0` / `EMPTY_VALUE`: per-line
 * year-to-date figures (only whole-statement YTD totals exist, via
 * `amountDetails.ytd`) and `payoutMethodLabel` (assemblable from PayoutSettings +
 * ExternalBankAccount but gated by a payer→payee bank-data grant, PLA-3198).
 */
export const selectorPaystubDetails = ({
  payStatement,
  workLogs,
  payeeAccount,
  payerAccount
}: Input): PaystubDetails => {
  const sourceIds = new Set(selectorPayStatementWorkLogIds(payStatement));

  const includedWorkLogs = workLogs.filter(
    workLog => workLog.id && sourceIds.has(workLog.id)
  );

  // Submitted/approved work logs in the period that this statement didn't pay.
  const excludedWorkLogs = workLogs.filter(
    workLog =>
      (workLog.status === WorkLogStatus.IN_REVIEW ||
        workLog.status === WorkLogStatus.APPROVED) &&
      workLog.id &&
      !sourceIds.has(workLog.id)
  );

  const { amountDetails } = payStatement;
  const grossPayThisPeriod = parsePayrollAmount(amountDetails?.grossPay);
  const netPayThisPeriod = parsePayrollAmount(amountDetails?.netPay);
  const grossPayYearToDate = parsePayrollAmount(amountDetails?.ytd?.grossPay);
  const netPayYearToDate = parsePayrollAmount(amountDetails?.ytd?.netPay);

  return {
    id: payStatement.id ?? "",
    paystubNumber: payStatement.payStatementNumber ?? payStatement.id ?? "",
    createdBy:
      payerAccount?.profile?.displayName ??
      payerAccount?.complianceEntity?.legalName ??
      EMPTY_VALUE,
    payPeriodRange: formatPayPeriodRange(
      payStatement.payPeriodStartDate,
      payStatement.payPeriodEndDate
    ),
    paidDate: payStatement.payDate,
    employeeName: selectEmployeeName(payeeAccount),
    employeeAddress: selectEmployeeAddress(payeeAccount),
    earnings: payStatement.earnings ?? [],
    grossPayThisPeriod,
    grossPayYearToDate,
    payeeTaxes: payStatement.payeeTaxes ?? [],
    deductionTotalThisPeriod: Math.max(
      grossPayThisPeriod - netPayThisPeriod,
      0
    ),
    deductionTotalYearToDate: Math.max(
      grossPayYearToDate - netPayYearToDate,
      0
    ),
    netPayThisPeriod,
    netPayYearToDate,
    payoutMethodLabel: EMPTY_VALUE,
    workLogs: includedWorkLogs,
    workLogsTotal: sumBy(includedWorkLogs, workLog =>
      parsePayrollAmount(workLog.totalAmount)
    ),
    excludedWorkLogs
  };
};
